import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Loader, Modal, notifications } from "@/shared/ui";
import { IconCheck, IconCopy, IconTrash } from "@tabler/icons-react";
import { favoriteAudio, unfavoriteAudio, setAudioShowStatus, deleteAudio, updateAudioChartsVisibility, getSongById, } from "@/shared/api/songs";
import { listShares, deleteShare, } from "@/shared/api/shares";
import { PersonaFromTrackModal } from "@/features/voice";
import styles from "./useTrackActions.module.scss";
export function useTrackActions(input) {
    const { language, audioId, songId, title } = input;
    const navigate = useNavigate();
    const ru = language === "ru";
    const [repeatBusy, setRepeatBusy] = React.useState(false);
    const [sharesOpen, setSharesOpen] = React.useState(false);
    const [allShares, setAllShares] = React.useState(null);
    const [copiedToken, setCopiedToken] = React.useState(null);
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [personaOpen, setPersonaOpen] = React.useState(false);
    const onFavorite = () => {
        if (audioId == null)
            return;
        const was = !!input.favorited;
        input.onPatch?.({ favorited: !was });
        void (async () => {
            try {
                if (was)
                    await unfavoriteAudio(audioId);
                else
                    await favoriteAudio(audioId);
            }
            catch (e) {
                console.error("toggle favorite failed", e);
                input.onPatch?.({ favorited: was });
                notifications.show({
                    color: "red",
                    title: ru ? "Ошибка" : "Error",
                    message: ru
                        ? "Не удалось обновить избранное."
                        : "Failed to update favorites.",
                });
            }
        })();
    };
    const onChart = () => {
        if (audioId == null)
            return;
        const was = !!input.isShowInCharts;
        input.onPatch?.({ isShowInCharts: !was });
        void (async () => {
            try {
                await updateAudioChartsVisibility(audioId, { in_charts: !was });
            }
            catch (e) {
                console.error("toggle charts failed", e);
                input.onPatch?.({ isShowInCharts: was });
                notifications.show({
                    color: "red",
                    title: ru ? "Ошибка" : "Error",
                    message: ru ? "Не удалось обновить чарт." : "Failed to update chart.",
                });
            }
        })();
    };
    const onHide = () => {
        if (audioId == null)
            return;
        const was = !!input.isShow;
        input.onPatch?.({ isShow: !was });
        void (async () => {
            try {
                await setAudioShowStatus(audioId, !was);
            }
            catch (e) {
                console.error("toggle show failed", e);
                input.onPatch?.({ isShow: was });
                notifications.show({
                    color: "red",
                    title: ru ? "Ошибка" : "Error",
                    message: ru
                        ? "Не удалось изменить видимость."
                        : "Failed to change visibility.",
                });
            }
        })();
    };
    const onDelete = () => setDeleteOpen(true);
    const confirmDelete = async () => {
        if (audioId == null)
            return;
        try {
            await deleteAudio(audioId);
            notifications.show({
                title: ru ? "Удалено" : "Deleted",
                message: ru ? "Трек удалён." : "Track removed.",
                color: "green",
            });
            input.onDeleted?.();
        }
        catch (e) {
            console.error("delete audio failed", e);
            notifications.show({
                color: "red",
                title: ru ? "Ошибка" : "Error",
                message: ru ? "Не удалось удалить трек." : "Failed to delete the track.",
            });
        }
        finally {
            setDeleteOpen(false);
        }
    };
    // «Повторить генерацию»: тянем полную песню (все параметры генерации) и
    // открываем экран настроек с предзаполнением через navigation-state.
    const onRepeat = () => {
        if (songId == null || repeatBusy)
            return;
        setRepeatBusy(true);
        void (async () => {
            try {
                const song = await getSongById(songId);
                navigate("/generate-song", { state: { repeatSongSettings: song } });
            }
            catch (e) {
                console.error("repeat generation failed", e);
                notifications.show({
                    color: "red",
                    title: ru ? "Ошибка" : "Error",
                    message: ru
                        ? "Не удалось загрузить настройки песни"
                        : "Failed to load song settings",
                });
            }
            finally {
                setRepeatBusy(false);
            }
        })();
    };
    const onShare = () => {
        if (audioId == null)
            return;
        setSharesOpen(true);
        if (allShares == null) {
            setAllShares("loading");
            void (async () => {
                try {
                    const shares = await listShares();
                    setAllShares(shares);
                }
                catch (e) {
                    console.error("listShares failed", e);
                    setAllShares("error");
                }
            })();
        }
    };
    const onVoicePersona = () => setPersonaOpen(true);
    const handleCopyShareLink = async (token) => {
        const url = `${window.location.origin}/s/${token}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedToken(token);
            window.setTimeout(() => setCopiedToken((cur) => (cur === token ? null : cur)), 2000);
        }
        catch {
            notifications.show({
                color: "red",
                title: ru ? "Не удалось скопировать" : "Could not copy",
                message: "",
            });
        }
    };
    const handleDeleteShare = async (shareId) => {
        try {
            await deleteShare(shareId);
            setAllShares((prev) => Array.isArray(prev) ? prev.filter((s) => s.id !== shareId) : prev);
        }
        catch (e) {
            console.error("deleteShare failed", e);
            notifications.show({
                color: "red",
                title: ru ? "Ошибка" : "Error",
                message: ru ? "Не удалось удалить ссылку." : "Failed to delete the link.",
            });
        }
    };
    const sharesBody = () => {
        if (allShares === "loading" || allShares == null) {
            return (_jsx("div", { className: styles.sharesState, children: _jsx(Loader, { size: "sm" }) }));
        }
        if (allShares === "error") {
            return (_jsx("div", { className: styles.sharesState, children: ru ? "Не удалось загрузить ссылки" : "Failed to load links" }));
        }
        const mine = allShares.filter((s) => s.audio_id === audioId);
        if (mine.length === 0) {
            return (_jsx("div", { className: styles.sharesState, children: ru
                    ? "Пока нет ссылок. Создайте страницу-подарок через «Поделиться»."
                    : "No links yet. Create a gift page via “Share”." }));
        }
        return (_jsx("ul", { className: styles.sharesList, children: mine.map((s) => {
                const url = `${window.location.origin}/s/${s.token}`;
                const copied = copiedToken === s.token;
                return (_jsxs("li", { className: styles.shareItem, children: [_jsx("a", { className: styles.shareUrl, href: url, target: "_blank", rel: "noreferrer", children: `/s/${s.token}` }), _jsx("button", { type: "button", className: styles.shareItemBtn, onClick: () => void handleCopyShareLink(s.token), "aria-label": ru ? "Копировать" : "Copy", children: copied ? _jsx(IconCheck, { size: 15 }) : _jsx(IconCopy, { size: 15 }) }), _jsx("button", { type: "button", className: `${styles.shareItemBtn} ${styles.shareItemDanger}`, onClick: () => void handleDeleteShare(s.id), "aria-label": ru ? "Удалить" : "Delete", children: _jsx(IconTrash, { size: 15 }) })] }, s.id));
            }) }));
    };
    const modals = (_jsxs(_Fragment, { children: [_jsx(Modal, { opened: sharesOpen, onClose: () => setSharesOpen(false), size: "sm", title: ru ? "Ссылки на подарок" : "Gift links", children: sharesBody() }), _jsxs(Modal, { opened: deleteOpen, onClose: () => setDeleteOpen(false), size: "sm", title: ru ? "Удалить трек?" : "Delete track?", children: [_jsx("p", { className: styles.deleteText, children: ru
                            ? "Трек будет удалён. Это действие необратимо."
                            : "The track will be deleted. This cannot be undone." }), _jsxs("div", { className: styles.deleteActions, children: [_jsx(Button, { variant: "ghost", size: "md", onClick: () => setDeleteOpen(false), children: ru ? "Отмена" : "Cancel" }), _jsx(Button, { variant: "primary", size: "md", onClick: () => void confirmDelete(), children: ru ? "Удалить" : "Delete" })] })] }), _jsx(PersonaFromTrackModal, { opened: personaOpen, onClose: () => setPersonaOpen(false), language: language, audioId: audioId ?? null, defaultName: title })] }));
    return {
        onFavorite,
        onChart,
        onHide,
        onDelete,
        onRepeat,
        onShare,
        onVoicePersona,
        repeatBusy,
        modals,
    };
}
